import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { Badge } from '../../components/ui/Badge';
import { Eye, EyeOff, Shield, Upload, FileText, X, Users, Search, Filter, Plus, Edit, Trash2, Download, Info, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import MockDataService from '../../services/MockDataService';
import DataTable from '../../components/ui/DataTable';
import {
  parseBulkUserFile,
  registerBulkUsers,
  formatBulkRegistrationSummary,
  downloadBulkUserTemplate,
} from '../../utils/bulkUserRegistration';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import { registrationService } from '../../api/registrationService';
import { userManagementService } from '../../api/userManagementService';
import { extractErrorMessage } from '../../utils/errorHandler';
import {
  PHONE_COUNTRY_OPTIONS,
  validatePhoneInput,
  buildPhoneApiFields,
  parsePhoneForForm,
} from '../../utils/phoneUtils';
import jsPDF from 'jspdf';
// Import jspdf-autotable - v5.x can be used as a function or extends prototype
import autoTable from 'jspdf-autotable';

const Register = () => {
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  /** One-shot: readonly until first focus blocks Chrome/email autofill on this filter field */
  const [userListSearchUnlocked, setUserListSearchUnlocked] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedUsers, setParsedUsers] = useState([]);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const bulkFileInputRef = useRef(null);
  const phoneValidationTimerRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    defaultPassword: '',
    role: '',
    department: ''
  });
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [phoneRegion, setPhoneRegion] = useState('US');
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneAvailable, setPhoneAvailable] = useState(null);

  useEffect(() => {
    loadRegisteredUsers();
    loadDepartments();
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await registrationService.getRoles();
      if (response?.data && Array.isArray(response.data)) {
        setRoles(response.data);
      } else {
        // Fallback to default roles if API fails
        setRoles([
          { value: 'super-admin', label: 'Super Admin', roleId: null, roleCode: 'super_admin' },
          { value: 'admin', label: 'Admin', roleId: null, roleCode: 'admin' },
          { value: 'agent', label: 'Agent', roleId: null, roleCode: 'agent' },
          { value: 'team-lead', label: 'Team Lead', roleId: null, roleCode: 'team_lead' },
          { value: 'customer', label: 'Customer', roleId: null, roleCode: 'customer' },
          { value: 'developer', label: 'Developer', roleId: null, roleCode: 'developer' }
        ]);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      // Fallback to default roles
      setRoles([
        { value: 'super-admin', label: 'Super Admin', roleId: null, roleCode: 'super_admin' },
        { value: 'admin', label: 'Admin', roleId: null, roleCode: 'admin' },
        { value: 'agent', label: 'Agent', roleId: null, roleCode: 'agent' },
        { value: 'team-lead', label: 'Team Lead', roleId: null, roleCode: 'team_lead' },
        { value: 'customer', label: 'Customer', roleId: null, roleCode: 'customer' },
        { value: 'developer', label: 'Developer', roleId: null, roleCode: 'developer' }
      ]);
      toast.error('Failed to load roles. Using default roles.');
    } finally {
      setLoadingRoles(false);
    }
  };

  const loadDepartments = async () => {
    setLoadingDepartments(true);
    try {
      // tenantId removed - backend will extract from JWT token
      const response = await registrationService.getDepartments();
      if (response && Array.isArray(response)) {
        setDepartments(response);
      } else if (response.data && Array.isArray(response.data)) {
        setDepartments(response.data);
      } else {
        console.warn('Unexpected departments response format:', response);
        toast.error('Failed to load departments');
      }
    } catch (error) {
      console.error('Error loading departments:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load departments';
      toast.error(errorMessage);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const loadRegisteredUsers = async () => {
    try {
      const response = await userManagementService.getUsers({ page: 0, size: 500 });
      let users = [];

      if (response?.data) {
        if (response.data.data?.content) {
          users = response.data.data.content;
        } else if (response.data.content) {
          users = response.data.content;
        } else if (Array.isArray(response.data)) {
          users = response.data;
        } else if (Array.isArray(response.data.data)) {
          users = response.data.data;
        }
      }

      const mappedUsers = users.map((user) => {
        let roleCode = user.roleCode || user.role || 'customer';
        if (roleCode) roleCode = roleCode.toLowerCase().replace(/_/g, '-');
        return {
          id: user.userId || user.id,
          name: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown',
          email: user.email || '',
          phone: user.phone || '',
          role: roleCode,
          status: user.isActive === false ? 'inactive' : 'active',
          createdAt: user.createdAt || user.created_at || new Date().toISOString(),
          profile: { phone: user.phone || '' },
        };
      });

      setRegisteredUsers(mappedUsers);
    } catch (error) {
      console.error('Error loading registered users:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Reset department when role changes away from agent/team-lead
      if (field === 'role' && value !== 'agent' && value !== 'team-lead') {
        newData.department = '';
      }
      
      return newData;
    });
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    // Reset email availability status when email changes
    if (field === 'email') {
      setEmailAvailable(null);
    }
    if (field === 'phone') {
      setPhoneAvailable(null);
      schedulePhoneValidation(value);
    }
  };

  useEffect(() => {
    return () => {
      if (phoneValidationTimerRef.current) {
        clearTimeout(phoneValidationTimerRef.current);
      }
    };
  }, []);

  const runPhoneValidation = async (phone) => {
    const trimmed = (phone || '').trim();
    if (!trimmed) {
      setPhoneAvailable(null);
      setErrors((prev) => ({ ...prev, phone: '' }));
      return;
    }

    const phoneValidation = validatePhoneInput(trimmed, phoneRegion);
    if (!phoneValidation.valid) {
      setPhoneAvailable(null);
      setErrors((prev) => ({ ...prev, phone: phoneValidation.message }));
      return;
    }

    const storedE164 = editingUser?.phoneE164 || editingUser?.phone;
    const currentFields = buildPhoneApiFields(trimmed, phoneRegion);
    if (editingUser && storedE164 && currentFields.phoneE164 === storedE164) {
      setPhoneAvailable(true);
      setErrors((prev) => ({ ...prev, phone: '' }));
      return;
    }

    setPhoneChecking(true);
    try {
      const response = await registrationService.checkPhoneAvailability(
        trimmed,
        phoneRegion,
        null,
        editingUser?.id || editingUser?.userId || null
      );
      if (response?.available === true) {
        setPhoneAvailable(true);
        setErrors((prev) => ({ ...prev, phone: '' }));
      } else if (response?.available === false) {
        setPhoneAvailable(false);
        setErrors((prev) => ({
          ...prev,
          phone: response?.message || 'Phone number is already registered',
        }));
      } else {
        setPhoneAvailable(null);
        setErrors((prev) => ({
          ...prev,
          phone: response?.message || 'Could not verify phone number',
        }));
      }
    } catch (error) {
      setPhoneAvailable(null);
      setErrors((prev) => ({ ...prev, phone: 'Failed to check phone availability' }));
    } finally {
      setPhoneChecking(false);
    }
  };

  const schedulePhoneValidation = (phone) => {
    if (phoneValidationTimerRef.current) {
      clearTimeout(phoneValidationTimerRef.current);
    }
    const trimmed = (phone || '').trim();
    if (!trimmed) {
      setPhoneAvailable(null);
      setErrors((prev) => ({ ...prev, phone: '' }));
      return;
    }
    phoneValidationTimerRef.current = setTimeout(() => {
      runPhoneValidation(trimmed);
    }, 500);
  };

  const handlePhoneBlur = (phone) => {
    if (phoneValidationTimerRef.current) {
      clearTimeout(phoneValidationTimerRef.current);
      phoneValidationTimerRef.current = null;
    }
    runPhoneValidation(phone);
  };

  const checkEmailAvailability = async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailAvailable(null);
      return;
    }

    // Skip check if editing existing user with same email
    if (editingUser && editingUser.email === email) {
      setEmailAvailable(true);
      return;
    }

    setEmailChecking(true);
    try {
      // tenantId removed - backend will extract from JWT token
      const response = await registrationService.checkEmailAvailability(email);
      
      if (response.available) {
        setEmailAvailable(true);
        setErrors(prev => ({ ...prev, email: '' }));
      } else {
        setEmailAvailable(false);
        setErrors(prev => ({ ...prev, email: 'Email is already registered' }));
      }
    } catch (error) {
      console.error('Email check error:', error);
      setEmailAvailable(null);
    } finally {
      setEmailChecking(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (emailAvailable === false) {
      newErrors.email = 'Email is already registered';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const phoneValidation = validatePhoneInput(formData.phone, phoneRegion);
      if (!phoneValidation.valid) {
        newErrors.phone = phoneValidation.message;
      } else if (phoneAvailable === false) {
        newErrors.phone = 'Phone number is already registered';
      }
    }

    // Password is only required when creating a new user, not when editing
    if (!editingUser) {
      if (!formData.defaultPassword.trim()) {
        newErrors.defaultPassword = 'Default password is required';
      } else if (formData.defaultPassword.length < 8) {
        newErrors.defaultPassword = 'Password must be at least 8 characters';
      }
    } else if (formData.defaultPassword.trim() && formData.defaultPassword.length < 8) {
      // If password is provided during edit, it must be at least 8 characters
      newErrors.defaultPassword = 'Password must be at least 8 characters';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    // Department is optional for all roles (including agent/team-lead)
    // No validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Split name into first and last name
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Find the selected role object to get roleId
      const selectedRole = roles.find(r => r.value === formData.role);
      if (!selectedRole || !selectedRole.roleId) {
        toast.error('Invalid role selected. Please select a valid role.');
        setLoading(false);
        return;
      }

      const phoneFields = buildPhoneApiFields(formData.phone, phoneRegion);

      const userData = {
        email: formData.email.trim().toLowerCase(),
        username: formData.email.trim().toLowerCase().split('@')[0], // Use email prefix as username
        fullName: formData.name.trim(),
        firstName: firstName,
        lastName: lastName,
        phone: phoneFields.phone,
        phoneRegion: phoneFields.phoneRegion,
        roleId: selectedRole.roleId, // Send roleId instead of role code
        // role: formData.role.toUpperCase().replace('-', '_'), // DEPRECATED: Kept for backward compatibility
        // tenantId removed - will be extracted from JWT token in backend (admin's tenant)
        defaultPassword: formData.defaultPassword.trim(),
      };

      // Include department if provided (optional for all roles)
      if (formData.department && formData.department.trim() !== '') {
        const deptId = parseInt(formData.department.trim(), 10);
        if (!isNaN(deptId) && deptId > 0) {
          userData.departmentId = Number(deptId);
          //console.log('✓ Setting departmentId:', userData.departmentId, 'for role:', formData.role);
        }
      }
      // Department is optional - if not provided, userData.departmentId will be undefined/null

      let response;
      if (editingUser) {
        const updatePhoneFields = buildPhoneApiFields(formData.phone, phoneRegion);
        const updateData = {
          fullName: formData.name.trim(),
          firstName,
          lastName,
          phone: updatePhoneFields.phone,
          phoneRegion: updatePhoneFields.phoneRegion,
          departmentId: formData.department ? parseInt(formData.department, 10) : null,
        };
        if (formData.defaultPassword.trim()) {
          updateData.password = formData.defaultPassword.trim();
        }
        response = await userManagementService.updateUser(editingUser.id || editingUser.userId, updateData);
        if (response?.data) {
          await loadRegisteredUsers();
        }
      } else {
        // Create new user via backend API (Super Admin can register all roles)
        response = await registrationService.registerBySuperAdmin(userData);
        
        if (response) {
          const newUser = {
            id: response.userId || response.id || Date.now(),
            name: userData.fullName,
            email: userData.email,
            phone: userData.phone,
            role: formData.role,
            status: 'active',
            createdAt: new Date().toISOString(),
            profile: {
              phone: userData.phone
            }
          };
          setRegisteredUsers(prev => [newUser, ...prev]);
        }
      }

      if (response || !editingUser) {
        toast.success(editingUser ? 'User updated successfully!' : 'User registered successfully!');
        setFormData({
          name: '',
          email: '',
          phone: '',
          defaultPassword: '',
          role: '',
          department: ''
        });
        setPhoneRegion('US');
        setPhoneAvailable(null);
        setErrors({});
        setEditingUser(null);
        setIsCreateModalOpen(false);
      } else {
        toast.error('Failed to save user. Please try again.');
      }
    } catch (error) {
      console.error('Error registering user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to register user. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    const parsedPhone = parsePhoneForForm(
      user.phoneE164 || user.phone || user.profile?.phone,
      'US'
    );
    setPhoneRegion(parsedPhone.region);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: parsedPhone.nationalNumber,
      defaultPassword: '', // Don't pre-fill password for security
      role: user.role || '',
      department: user.departmentId ? user.departmentId.toString() : ''
    });
    setIsCreateModalOpen(true);
  };

  const handleDeleteUser = (userId) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      const response = await MockDataService.deleteUser(userToDelete);
      
      if (response.success) {
        toast.success('User deleted successfully!');
        setRegisteredUsers(prev => prev.filter(user => user.id !== userToDelete));
      } else {
        toast.error(response.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('An error occurred while deleting the user');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const resetBulkUploadState = () => {
    setSelectedFile(null);
    setParsedUsers([]);
    setBulkUploadResult(null);
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv');
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (!isCSV && !isExcel) {
      toast.error('Please upload a CSV or Excel (.xlsx, .xls) file.');
      return;
    }

    setSelectedFile(file);
    setUploadingFile(true);
    setBulkUploadResult(null);

    try {
      const users = await parseBulkUserFile(file);

      if (users.length === 0) {
        toast.error('No valid user data found. Ensure name and email columns are present.');
        setSelectedFile(null);
        return;
      }

      setParsedUsers(users);
      toast.success(`File processed. Found ${users.length} user(s) ready to register.`);
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error(extractErrorMessage(error, 'Failed to process the uploaded file.'));
      setSelectedFile(null);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleBulkCreateUsers = async () => {
    if (parsedUsers.length === 0) return;

    if (!roles.some((r) => r.roleId)) {
      toast.error('Roles are not loaded. Please refresh the page and try again.');
      return;
    }

    setLoading(true);
    setBulkUploadResult(null);

    try {
      const result = await registerBulkUsers(parsedUsers, roles, {
        checkEmail: registrationService.checkEmailAvailability,
        checkPhone: registrationService.checkPhoneAvailability,
        registerUser: registrationService.registerBySuperAdmin,
      });

      setBulkUploadResult(result);

      if (result.created.length > 0) {
        await loadRegisteredUsers();
      }

      const summary = formatBulkRegistrationSummary(result);
      if (result.created.length > 0 && result.alreadyExists.length === 0 && result.failed.length === 0) {
        toast.success(`Bulk upload successful. ${summary}.`);
        resetBulkUploadState();
        setIsBulkUploadModalOpen(false);
      } else if (result.created.length > 0) {
        toast.success(`Partially completed: ${summary}.`);
      } else if (result.alreadyExists.length > 0 && result.failed.length === 0) {
        toast.error('No new users were created. All uploaded emails already exist.');
      } else {
        toast.error(`Bulk upload finished with issues: ${summary}.`);
      }
    } catch (error) {
      console.error('Error processing bulk upload:', error);
      toast.error(extractErrorMessage(error, 'Bulk upload failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Generate and download sample CSV file (includes location columns)
  const downloadSampleFile = () => {
    downloadBulkUserTemplate();
  };

  const filteredUsers = registeredUsers.filter(user => {
    const matchesSearch = user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    return matchesSearch && matchesStatus && matchesRole;
  });

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'superadmin': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'agent': return 'bg-green-100 text-green-800 border-green-200';
      case 'enduser': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'suspended': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Export users to PDF with pagination
  const handleExportUsers = async () => {
    if (filteredUsers.length === 0) {
      toast.error('No users to export.');
      return;
    }

    try {
      // Use landscape orientation for better table fit
      const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const tableStartY = 35;

      // Header
      doc.setFontSize(16);
      doc.setTextColor(93, 82, 146); // Super Admin primary color
      doc.text('Registered Users List', margin, 15);
      
      doc.setFontSize(9);
      doc.setTextColor(88, 88, 88);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, 22);
      doc.text(`Total Users: ${filteredUsers.length}`, pageWidth - margin - 40, 22);

      // Prepare table data
      const tableData = filteredUsers.map(user => [
        user.name || 'N/A',
        user.email || 'N/A',
        user.phone || user.profile?.phone || 'N/A',
        user.role || 'N/A',
        user.status || 'N/A',
        user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'
      ]);

      // Table headers
      const headers = [['Name', 'Email', 'Phone', 'Role', 'Status', 'Registered']];

      // Use autoTable with automatic pagination
      // For v5.x, autoTable can be called as a function with doc as first param, or as doc.autoTable()
      const autoTableConfig = {
        head: headers,
        body: tableData,
        startY: tableStartY,
        margin: { top: tableStartY, right: margin, bottom: 25, left: margin },
        styles: {
          fontSize: 7,
          cellPadding: 2,
          textColor: [37, 37, 37], // #252525
          lineColor: [230, 230, 230], // #E6E6E6
          lineWidth: 0.3,
        },
        headStyles: {
          fillColor: [93, 82, 146], // Super Admin primary color
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 8,
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250], // #F5F7FA
        },
        columnStyles: {
          0: { cellWidth: 50 }, // Name
          1: { cellWidth: 60 }, // Email
          2: { cellWidth: 40 }, // Phone
          3: { cellWidth: 30 }, // Role
          4: { cellWidth: 25 }, // Status
          5: { cellWidth: 30 }, // Registered
        },
        didDrawPage: (data) => {
          // Get current page number
          const currentPage = data.pageNumber || doc.internal.getCurrentPageInfo().pageNumber;
          const totalPages = doc.internal.getNumberOfPages();
          
          // Footer with pagination
          doc.setFontSize(8);
          doc.setTextColor(136, 136, 136); // #888888
          const footerText = `Page ${currentPage} of ${totalPages} | Total: ${filteredUsers.length} users`;
          doc.text(
            footerText,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        },
      };

      // Call autoTable - try as function first, then as method
      if (typeof autoTable === 'function') {
        autoTable(doc, autoTableConfig);
      } else if (typeof doc.autoTable === 'function') {
        doc.autoTable(autoTableConfig);
      } else {
        throw new Error('autoTable is not available. Please ensure jspdf-autotable is properly installed.');
      }

      // Save PDF
      const fileName = `users_list_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      toast.success(`Users list exported successfully as ${fileName}`);
    } catch (error) {
      console.error('Error exporting users:', error);
      console.error('Error details:', error.message, error.stack);
      toast.error(`Failed to export users: ${error.message || 'Please try again.'}`);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'User Name',
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-semibold">
              {row.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <div className="font-semibold">{row.name || 'N/A'}</div>
            <div className="text-sm text-gray-500">{row.email || 'N/A'}</div>
          </div>
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (value, row) => (
        <div className="text-sm text-gray-600">{row.phone || row.profile?.phone || 'N/A'}</div>
      )
    },
    {
      key: 'role',
      label: 'Role',
      render: (value) => (
        <Badge variant="outline" className={getRoleBadgeColor(value)}>
          {value || 'N/A'}
        </Badge>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge variant="outline" className={getStatusBadgeColor(value)}>
          {value || 'active'}
        </Badge>
      )
    },
    {
      key: 'createdAt',
      label: 'Registered',
      render: (value) => (
        <span className="text-sm text-gray-600">
          {value ? new Date(value).toLocaleDateString() : 'N/A'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            title="Edit User"
            onClick={() => handleEditUser(row)}
            className="text-blue-600 hover:text-blue-700"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            title="Delete User"
            onClick={() => handleDeleteUser(row.id)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-12 px-6 overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/background-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(2px)',
            opacity: 0.3
          }}
        ></div>
        <div className="absolute inset-0 bg-white/70"></div>

        <div className="container mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div className="space-y-2 sm:space-y-3">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 border border-primary/20 rounded-full">
                <span className="text-xs sm:text-sm font-semibold text-primary">User Management</span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground drop-shadow-lg">
                Register User
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground">
                Manage user registrations, create new accounts, and bulk import users
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <Button 
                variant="outline"
                className="border-2 hover:bg-primary hover:text-primary-foreground w-full sm:w-auto"
                onClick={handleExportUsers}
                disabled={filteredUsers.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button 
                variant="outline"
                className="border-2 hover:bg-primary hover:text-primary-foreground w-full sm:w-auto"
                onClick={() => setIsBulkUploadModalOpen(true)}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload File
              </Button>
              <Button 
                className="gradient-primary text-primary-foreground shadow-elegant hover:shadow-glow w-full sm:w-auto"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="p-4 sm:p-6 relative">
        {/* Search and Filter */}
        <Card className="mb-6 gradient-card group hover:shadow-elegant transition-smooth border-2 hover:border-primary/30">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  id="register-user-directory-query"
                  name="register-user-directory-query"
                  type="search"
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  readOnly={!userListSearchUnlocked}
                  onFocus={() => setUserListSearchUnlocked(true)}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value || '')}
                  className="pl-11 h-11 bg-card border-2 border-border focus-visible:ring-primary"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-muted-foreground" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="flex-1 px-3 py-2 h-11 bg-card border-2 border-border rounded-md focus:ring-2 focus:ring-primary text-sm font-medium"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowFilterModal(!showFilterModal)}
                className="h-11 bg-card border-2 hover:bg-primary hover:text-primary-foreground"
              >
                <Filter className="h-4 w-4 mr-2" />
                More Filters
              </Button>
            </div>
            <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Showing <strong className="text-foreground">{filteredUsers.length}</strong> of <strong className="text-foreground">{registeredUsers.length}</strong> users
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="group hover:shadow-luxury transition-smooth border-2 hover:border-primary/30 overflow-hidden relative gradient-card">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <CardHeader className="relative z-10">
            <div className="flex items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-accent shadow-glow">
                  <Users className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>{filteredUsers.length} users registered</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <DataTable
              data={filteredUsers}
              columns={columns}
              pagination={true}
              searchable={false}
              sortable={true}
              itemsPerPage={10}
              showItemsPerPageSelector={true}
            />
          </CardContent>
        </Card>

        {/* Create User Modal */}
        {isCreateModalOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-[112]"
              onClick={() => {
                setIsCreateModalOpen(false);
                setEditingUser(null);
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  defaultPassword: '',
                  role: '',
                  department: ''
                });
                setPhoneRegion('US');
                setPhoneAvailable(null);
                setErrors({});
              }}
            />
            <Dialog open={isCreateModalOpen} onOpenChange={(open) => {
              if (!open) {
                setIsCreateModalOpen(false);
                setEditingUser(null);
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  defaultPassword: '',
                  role: ''
                });
                setPhoneRegion('US');
                setPhoneAvailable(null);
                setErrors({});
              } else {
                setIsCreateModalOpen(true);
              }
            }}>
              <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="gradient-primary p-1 flex flex-col h-full overflow-hidden">
                  <div className="bg-card flex flex-col h-full overflow-hidden">
                    {/* Fixed Header */}
                    <DialogHeader className="mb-4 flex-shrink-0 p-6 pb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-accent shadow-glow">
                          <Plus className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <DialogTitle className="text-2xl font-bold">
                          {editingUser ? 'Edit User' : 'Create New User'}
                        </DialogTitle>
                      </div>
                    </DialogHeader>
                    
                    {/* Content Area - Scrollable */}
                    <div className="flex-1 px-6 overflow-y-auto overflow-x-hidden relative">
                      <div className="space-y-4 pb-4">
                        <div>
                          <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e?.target?.value || '')}
                            placeholder="Enter full name"
                            className={errors.name ? 'border-red-500' : ''}
                          />
                          {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
                        </div>
                        <div>
                          <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                          <div className="relative">
                            <Input
                              id="email"
                              type="email"
                              value={formData.email}
                              onChange={(e) => handleInputChange('email', e?.target?.value || '')}
                              onBlur={(e) => checkEmailAvailability(e.target.value)}
                              placeholder="Enter email address"
                              className={`${errors.email ? 'border-red-500' : ''} ${emailAvailable === true ? 'border-green-500' : ''} ${emailChecking ? 'pr-10' : emailAvailable !== null ? 'pr-10' : ''}`}
                              disabled={emailChecking}
                            />
                            {emailChecking && (
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                              </div>
                            )}
                            {!emailChecking && emailAvailable === true && (
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                            {!emailChecking && emailAvailable === false && (
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <AlertCircle className="h-5 w-5 text-red-500" />
                              </div>
                            )}
                          </div>
                          {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
                          {emailAvailable === true && !errors.email && (
                            <p className="text-sm text-green-600 mt-1">Email is available</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="phone">Phone Number <span className="text-red-500">*</span></Label>
                          <div className="flex gap-2">
                            <div className="w-32 relative">
                              <Select
                                value={phoneRegion}
                                onValueChange={(value) => {
                                  setPhoneRegion(value);
                                  setPhoneAvailable(null);
                                  if (formData.phone.trim()) {
                                    schedulePhoneValidation(formData.phone);
                                  }
                                }}
                              >
                                <SelectTrigger className="h-10 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                            <SelectContent className="max-h-[180px] overflow-y-auto z-[80]">
                              {PHONE_COUNTRY_OPTIONS.map((code) => (
                                <SelectItem key={code.region} value={code.region}>
                                  {code.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                              </Select>
                            </div>
                            <div className="flex-1 relative">
                              <Input
                                id="phone"
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e?.target?.value || '')}
                                onBlur={(e) => handlePhoneBlur(e.target.value)}
                                placeholder="Enter phone number"
                                className={`${errors.phone ? 'border-red-500' : ''} ${phoneAvailable === true ? 'border-green-500' : ''} ${phoneChecking || phoneAvailable !== null ? 'pr-10' : ''}`}
                                disabled={phoneChecking}
                              />
                              {phoneChecking && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                                </div>
                              )}
                              {!phoneChecking && phoneAvailable === true && !errors.phone && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              )}
                              {!phoneChecking && (phoneAvailable === false || errors.phone) && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                  <AlertCircle className="h-5 w-5 text-red-500" />
                                </div>
                              )}
                            </div>
                          </div>
                          {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
                          {phoneAvailable === true && !errors.phone && (
                            <p className="text-sm text-green-600 mt-1">Phone number is available</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="defaultPassword">
                            Default Password {!editingUser && <span className="text-red-500">*</span>}
                            {editingUser && <span className="text-gray-500 text-xs ml-2">(Leave blank to keep current password)</span>}
                          </Label>
                          <div className="relative">
                            <Input
                              id="defaultPassword"
                              type={showPassword ? 'text' : 'password'}
                              value={formData.defaultPassword}
                              onChange={(e) => handleInputChange('defaultPassword', e?.target?.value || '')}
                              placeholder="Enter default password (min 8 characters)"
                              className={errors.defaultPassword ? 'border-red-500 pr-10' : 'pr-10'}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                            >
                              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                            </button>
                          </div>
                          {errors.defaultPassword && <p className="text-sm text-red-600 mt-1">{errors.defaultPassword}</p>}
                          <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
                        </div>
                        <div className="relative">
                          <Label htmlFor="role">Role <span className="text-red-500">*</span></Label>
                          <Select
                            value={formData.role}
                            onValueChange={(value) => handleInputChange('role', value)}
                          >
                            <SelectTrigger className={errors.role ? 'border-red-500 h-10' : 'h-10'}>
                              <SelectValue placeholder="Select a role" />
                            </SelectTrigger>
                            <SelectContent 
                              forceDownward={true}
                              className="max-h-[200px] z-[100]"
                              style={{ 
                                maxHeight: '200px'
                              }}
                            >
                              {roles.map((role) => (
                                <SelectItem key={role.value} value={role.value}>
                                  <div className="flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-gray-500" />
                                    {role.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.role && <p className="text-sm text-red-600 mt-1">{errors.role}</p>}
                        </div>
                        {/* Department Dropdown - Optional for all roles */}
                        {/* COMMENTED OUT: Department field hidden per requirement */}
                        {/* {formData.role && formData.role !== 'customer' && (
                          <div className="relative">
                            <Label htmlFor="department">
                              Department
                            </Label>
                            <Select
                              value={formData.department}
                              onValueChange={(value) => handleInputChange('department', value)}
                            >
                              <SelectTrigger className={errors.department ? 'border-red-500 h-10' : 'h-10'}>
                                <SelectValue placeholder="Select department" />
                              </SelectTrigger>
                              <SelectContent className="max-h-[180px] overflow-y-auto z-[80]">
                                {loadingDepartments ? (
                                  <SelectItem value="" disabled>Loading departments...</SelectItem>
                                ) : departments.length === 0 ? (
                                  <SelectItem value="" disabled>No departments available</SelectItem>
                                ) : (
                                  departments.map((dept) => (
                                    <SelectItem key={dept.id} value={dept.id.toString()}>
                                      {dept.name} ({dept.code})
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                            {errors.department && <p className="text-sm text-red-600 mt-1">{errors.department}</p>}
                          </div>
                        )} */}
                      </div>
                    </div>
                    
                    {/* Fixed Footer */}
                    <div className="flex justify-end space-x-2 pt-4 border-t px-6 pb-6 flex-shrink-0">
                      <Button variant="outline" onClick={() => {
                        setIsCreateModalOpen(false);
                        setEditingUser(null);
                        setFormData({
                          name: '',
                          email: '',
                          phone: '',
                          defaultPassword: '',
                          role: '',
                          department: ''
                        });
                        setPhoneRegion('US');
                        setPhoneAvailable(null);
                        setErrors({});
                      }}>
                        Cancel
                      </Button>
                      <Button 
                        className="gradient-primary text-primary-foreground shadow-elegant hover:shadow-glow"
                        onClick={handleCreateUser}
                        disabled={loading}
                      >
                        {loading ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update User' : 'Create User')}
                      </Button>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCreateModalOpen(false);
                    setEditingUser(null);
                    setFormData({
                      name: '',
                      email: '',
                      phone: '',
                      defaultPassword: '',
                      role: '',
                      department: ''
                    });
                    setPhoneRegion('US');
                    setPhoneAvailable(null);
                    setErrors({});
                  }}
                  className="absolute right-4 top-4 z-[80] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background"
                  type="button"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </button>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Bulk Upload Modal */}
        {isBulkUploadModalOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-[112]"
              onClick={() => {
                if (!uploadingFile && !loading) {
                  setIsBulkUploadModalOpen(false);
                  resetBulkUploadState();
                }
              }}
            />
            <Dialog open={isBulkUploadModalOpen} onOpenChange={(open) => {
              if (!open && !uploadingFile && !loading) {
                setIsBulkUploadModalOpen(false);
                resetBulkUploadState();
              }
            }}>
              <DialogContent className="w-[95vw] sm:w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="gradient-primary p-1 flex flex-col h-full overflow-hidden rounded-lg">
                  <div className="bg-card flex flex-col h-full overflow-hidden rounded-md p-5 sm:p-6 gap-4">
                    {/* Fixed Header */}
                    <DialogHeader className="flex-shrink-0 p-0 m-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-accent shadow-glow">
                          <Upload className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <DialogTitle className="text-2xl font-bold">Bulk User Registration</DialogTitle>
                      </div>
                    </DialogHeader>
                    
                    {/* Scrollable Content Area */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 -mx-1 px-1">
                      <div className="space-y-4 pb-1">
                      {/* File Format Instructions */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-blue-900 mb-2">File Format Requirements</h4>
                            <div className="text-xs text-blue-800 space-y-1">
                              <p><strong>Supported Formats:</strong> CSV (.csv) or Excel (.xlsx, .xls)</p>
                              <p><strong>Note:</strong> PDF files are not fully supported. Please use CSV or Excel format.</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expected Columns Info */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-amber-900 mb-2">Expected Columns</h4>
                            <div className="text-xs text-amber-800 space-y-1">
                              <p><strong>Required Columns:</strong></p>
                              <ul className="list-disc list-inside ml-2 space-y-0.5">
                                <li><strong>name</strong> (or "full name", "fullname") - User's full name</li>
                                <li><strong>email</strong> (or "email address") - Valid email address</li>
                              </ul>
                              <p className="mt-2"><strong>Optional Columns:</strong></p>
                              <ul className="list-disc list-inside ml-2 space-y-0.5">
                                <li><strong>phone</strong> (or "phone number", "mobile") - Phone number with country code</li>
                                <li><strong>role</strong> - User role (super-admin, admin, agent, customer). Default: customer</li>
                                <li><strong>password</strong> (or "default password") - Minimum 8 characters. Default: Auto-generated</li>
                                <li><strong>region</strong> - Region name (e.g. Nairobi Province). Required for agent, customer, team-lead</li>
                                <li><strong>county</strong> - County name (e.g. Nairobi). Required for agent, customer, team-lead</li>
                                <li><strong>subCounty</strong> - Sub-county name (optional, e.g. Westlands)</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sample File Download */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <FileText className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-green-900 mb-1">Download Sample Template</h4>
                              <p className="text-xs text-green-800">
                                Download a sample Excel file with the correct format. Edit it with your user data and upload.
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={downloadSampleFile}
                            className="ml-4 border-green-300 text-green-700 hover:bg-green-100"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download Template
                          </Button>
                        </div>
                      </div>

                      {parsedUsers.length === 0 ? (
                        <>
                          <div>
                            <Label>Upload File</Label>
                            <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors">
                              <input
                                ref={bulkFileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleFileSelect}
                                className="hidden"
                                id="bulk-file-upload"
                                disabled={uploadingFile}
                              />
                              <label htmlFor="bulk-file-upload" className="cursor-pointer">
                                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-sm font-medium text-gray-700 mb-2">
                                  {uploadingFile ? 'Processing file...' : 'Click to upload or drag and drop'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  CSV or Excel (.xlsx, .xls) files only
                                </p>
                              </label>
                            </div>
                            {selectedFile && (
                              <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                                <FileText className="h-4 w-4" />
                                <span>{selectedFile.name}</span>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <p className="text-sm font-medium text-green-800">
                              File processed successfully! Found {parsedUsers.length} user(s) ready to register.
                            </p>
                          </div>
                          {bulkUploadResult && (
                            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                              <p className="text-sm font-semibold text-gray-900">Upload result</p>
                              {bulkUploadResult.created.length > 0 && (
                                <div>
                                  <p className="text-sm font-medium text-green-700 mb-1">
                                    {bulkUploadResult.created.length} user(s) registered successfully:
                                  </p>
                                  <ul className="max-h-24 overflow-y-auto text-xs text-green-800 list-disc list-inside space-y-0.5">
                                    {bulkUploadResult.created.map((u) => (
                                      <li key={u.email}>
                                        {u.name ? <>{u.name} - {u.email}</> : u.email}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {bulkUploadResult.alreadyExists.length > 0 && (
                                <div>
                                  <p className="text-sm font-medium text-amber-800 mb-1">
                                    {bulkUploadResult.alreadyExists.length} user(s) already exist (not created):
                                  </p>
                                  <ul className="max-h-24 overflow-y-auto text-xs text-amber-900 list-disc list-inside space-y-0.5">
                                    {bulkUploadResult.alreadyExists.map((u) => (
                                      <li key={u.email}>
                                        {u.email}
                                        {u.reason ? <> - {u.reason}</> : null}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {bulkUploadResult.failed.length > 0 && (
                                <div>
                                  <p className="text-sm font-medium text-red-700 mb-1">
                                    {bulkUploadResult.failed.length} user(s) failed:
                                  </p>
                                  <ul className="max-h-24 overflow-y-auto text-xs text-red-800 list-disc list-inside space-y-0.5">
                                    {bulkUploadResult.failed.map((u) => (
                                      <li key={u.email}>
                                        {u.email} - {u.reason}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="border border-gray-200 rounded-lg overflow-hidden">

                            <div className="overflow-x-auto max-h-64 overflow-y-auto -mx-1">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Name</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Email</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Role</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                  {parsedUsers.map((user, index) => (
                                    <tr key={index}>
                                      <td className="px-4 py-2 whitespace-nowrap">{user.name}</td>
                                      <td className="px-4 py-2 whitespace-nowrap text-xs">{user.email}</td>
                                      <td className="px-4 py-2 whitespace-nowrap">
                                        <Badge variant="outline" className="text-xs">{user.role || 'customer'}</Badge>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </>
                      )}
                      </div>
                    </div>
                    
                    {/* Fixed Footer */}
                    {parsedUsers.length > 0 && (
                      <div className="flex justify-end space-x-2 pt-4 border-t flex-shrink-0">
                        <Button
                          variant="outline"
                          onClick={resetBulkUploadState}
                        >
                          Cancel
                        </Button>
                        <Button 
                          className="gradient-primary text-primary-foreground shadow-elegant hover:shadow-glow"
                          onClick={handleBulkCreateUsers}
                          disabled={loading}
                        >
                          {loading ? 'Creating...' : `Create ${parsedUsers.length} Users`}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!uploadingFile && !loading) {
                      setIsBulkUploadModalOpen(false);
                      resetBulkUploadState();
                    }
                  }}
                  className="absolute right-4 top-4 z-[80] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background"
                  type="button"
                  disabled={uploadingFile}
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </button>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Filter Modal */}
        {showFilterModal && (
          <>
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-[112]"
              onClick={() => setShowFilterModal(false)}
            />
            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[125] bg-white rounded-lg shadow-lg p-6 w-96">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Advanced Filters</h3>
                <button 
                  onClick={() => setShowFilterModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Role</Label>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md mt-1"
                  >
                    <option value="all">All Roles</option>
                    <option value="superadmin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="agent">Agent</option>
                    <option value="enduser">End User</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => {
                    setRoleFilter('all');
                    setStatusFilter('all');
                    setSearchTerm('');
                    setShowFilterModal(false);
                  }}>
                    Clear Filter
                  </Button>
                  <Button onClick={() => setShowFilterModal(false)}>
                    Apply Filter
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setUserToDelete(null);
          }}
          onConfirm={confirmDeleteUser}
          title="Delete User"
          message="Are you sure you want to delete this user? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </div>
  );
};

export default Register;
